// react and js
import React from "react";
import PropTypes from "prop-types";

// mui
import { DialogTitle, DialogContent, DialogActions, Typography, IconButton, Box, Stack } from "@mui/material";

// components
import ModalDialog from "../../../components/ModalDialog";
import Iconify from "../../../components/Iconify";

// theme
import { ModalActionsLoadingButton } from "../../../theme/styled";

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export default function HandleConfirmationDialog({ open, onClose, title, message, onConfirm, disabled = false, confirmText = "Delete", cancelText = "Cancel" }) {

  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  HandleConfirmationDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    onConfirm: PropTypes.any,
    disabled: PropTypes.bool,
    confirmText: PropTypes.string,
    cancelText: PropTypes.string,
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const [stableTitle, setStableTitle] = React.useState(title);
  const [stableMessage, setStableMessage] = React.useState(message);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  React.useEffect(() => {
    if (open) {
      setStableTitle(title);
      setStableMessage(message);
    }
  }, [open, title, message]);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  // ----------------------------------------------------------------------
  // Main component
  // ----------------------------------------------------------------------
  return (
    <ModalDialog
      open={open}
      handleClose={onClose}
      scroll="paper"
      maxWidth="sm"
      fullWidth={false}
      PaperProps={{
        sx: {
          width: "90vw",
          maxWidth: "500px",
          margin: "auto",
        },
      }}
      outlet={
        <>
          <DialogTitle
            id="dialog-title"
            sx={{
              background: "#F2F2F2",
              textTransform: "none",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              alignContent: "center",
              py: 1,
              pl: 2,
              pr: 1,
            }}
          >
            <Typography variant="contentBig">{stableTitle}</Typography>

            <IconButton onClick={onClose} sx={{ borderRadius: "10px" }}>
              <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
            </IconButton>
          </DialogTitle>

          <DialogContent
            dividers
            sx={{
              background: "#F2F2F2",
              borderTop: "1px solid #C8C8C8",
              borderBottom: "0px solid #C8C8C8",
              px: 2,
              pt: 2,
              pb: 1,
              width: "100%",
              boxSizing: "border-box",
              "&::-webkit-scrollbar": {
                width: "16px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#e0e0e0",
                borderRadius: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888888",
                borderRadius: "6px",
                border: "2px solid #e0e0e0",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#666666",
              },
              // Firefox scrollbar styling
              scrollbarWidth: "thin",
              scrollbarColor: "#888888 #e0e0e0",
            }}
          >
            <Stack spacing={2}>
              <Typography variant="contentMiddle">{stableMessage}</Typography>
            </Stack>
          </DialogContent>

          <DialogActions
            sx={{
              background: "#F2F2F2",
              p: 2,
              pt: 1,
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "column",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
              <ModalActionsLoadingButton fullWidth size="large" onClick={onClose} sx={{ p: 1 }}>
                {cancelText}
              </ModalActionsLoadingButton>

              <ModalActionsLoadingButton 
                fullWidth 
                size="large" 
                variant="contained" 
                onClick={handleConfirm} 
                autoFocus 
                disabled={disabled}
                sx={{ p: 1 }}
              >
                {confirmText}
              </ModalActionsLoadingButton>
            </Box>
          </DialogActions>
        </>
      }
    />
  );
}
